import { useEffect, useRef, useState } from "react";
import { onIdTokenChanged } from "firebase/auth";
import QRCode from "qrcode";
import { auth } from "@/lib/firebase";
import { beginTotpEnrollment, enrollTotp, getMfaFactors, reauthenticateAccount, removeMfaFactor, resolveTotpChallenge } from "@/lib/accountSecurity";
import { Button } from "@/components/ax/Button";
import { Field } from "@/components/ax/Field";
import { MfaChallenge } from "./MfaChallenge";
import { useI18n } from "@/i18n";
import styles from "./AccountVisual.module.css";

export function MfaSettings({ onBusy, disabled = false }) {
  const { t } = useI18n();
  const [factors, setFactors] = useState(getMfaFactors), [mode, setMode] = useState(null), [target, setTarget] = useState(null);
  const [password, setPassword] = useState(""), [busy, setBusy] = useState(false), [error, setError] = useState(null), [challenge, setChallenge] = useState(null);
  const [setup, setSetup] = useState(null), [qr, setQr] = useState(null), [code, setCode] = useState(""), [success, setSuccess] = useState(null);
  const owner = useRef(auth.currentUser?.uid);
  useEffect(() => onIdTokenChanged(auth, (user) => {
    if (user?.uid !== owner.current) { setSetup(null); setPassword(""); setCode(""); setQr(null); setChallenge(null); setMode(null); owner.current = user?.uid; }
    setFactors(getMfaFactors());
  }), []);
  useEffect(() => { onBusy(busy); return () => onBusy(false); }, [busy, onBusy]);
  useEffect(() => {
    let alive = true;
    if (setup) QRCode.toDataURL(setup.uri, { width: 180, margin: 2, errorCorrectionLevel: "M" }).then((value) => { if (alive) setQr(value); }).catch(() => { if (alive) setError(t("mfa.unavailable")); });
    else setQr(null);
    return () => { alive = false; };
  }, [setup, t]);
  useEffect(() => {
    if (!setup) return;
    const expires = new Date(setup.secret.enrollmentCompletionDeadline).getTime();
    if (!Number.isFinite(expires)) return;
    const timer = setTimeout(() => { setSetup(null); setQr(null); setCode(""); setMode(null); setError(t("mfa.expired")); }, Math.max(0, expires - Date.now()));
    return () => clearTimeout(timer);
  }, [setup, t]);
  const reset = () => { setMode(null); setTarget(null); setPassword(""); setChallenge(null); setSetup(null); setQr(null); setCode(""); setError(null); };
  const report = (result) => setError(t(result.error === "limit" ? "mfa.factorLimit" : result.error === "unavailable" ? "mfa.unavailable" : result.error === "code" ? "mfa.codeFailed" : "mfa.actionFailed"));
  const afterReauth = async () => {
    setPassword(""); setChallenge(null);
    const result = mode === "remove" ? await removeMfaFactor(target) : await beginTotpEnrollment();
    if (result.ok) {
      if (mode === "remove") { reset(); setFactors(getMfaFactors()); setSuccess(t("mfa.removed")); }
      else { setSetup(result); setMode("setup"); }
    } else report(result);
    return result;
  };
  const reauth = async (event) => {
    event.preventDefault(); if (busy) return;
    setBusy(true); setError(null);
    const result = await reauthenticateAccount(password);
    if (result.mfa) { setPassword(""); setChallenge(result.mfa); }
    else if (result.ok) await afterReauth(); else report(result);
    setBusy(false);
  };
  const verifyReauth = async (uid, value) => {
    setBusy(true);
    const result = await resolveTotpChallenge(challenge, uid, value);
    if (result.ok) await afterReauth();
    setBusy(false); return result;
  };
  const enroll = async (event) => {
    event.preventDefault(); if (busy || code.length !== 6) return;
    setBusy(true); setError(null);
    const result = await enrollTotp(setup?.secret, code, `AXORIS Authenticator ${factors.length + 1}`);
    if (result.ok) { reset(); setFactors(getMfaFactors()); setSuccess(t("mfa.enabled")); } else report(result);
    setBusy(false);
  };
  const start = (next, uid = null) => { reset(); setSuccess(null); setTarget(uid); setMode(next); };
  const hasPassword = auth.currentUser?.providerData.some((item) => item.providerId === "password");
  const hasTotp = factors.some((factor) => factor.factorId === "totp");
  return <div className={styles.mfa} data-testid="mfa-settings">
    <p className={styles.totpTitle}>{t("mfa.title")}</p>
    <p className={styles.totpNote} data-testid="mfa-status">{t(factors.length ? "mfa.active" : "mfa.inactive")}</p>
    <p className={styles.totpNote} data-testid="mfa-optional-note">{t("mfa.optionalNote")}</p>
    <p className={styles.totpNote}>{t("mfa.backupNote")}</p>
    {auth.emulatorConfig && <p className={styles.totpNote} data-testid="mfa-preview-note">{t("mfa.localUnavailable")}</p>}
    {!mode && <div className={styles.securityActions}>
      {!hasTotp && <Button variant="secondary" disabled={disabled} onClick={() => start("enroll")} data-testid="mfa-start-button">{t("mfa.enable")}</Button>}
      {factors.map((factor, index) => <div key={factor.uid}><span className={styles.totpNote}>{factor.displayName || `${t("mfa.authenticator")} ${index + 1}`}</span><Button variant="ghost" disabled={disabled} onClick={() => start("remove", factor.uid)}>{t("mfa.disable")}</Button></div>)}
    </div>}
    {mode && !setup && !challenge && <form onSubmit={reauth} className="mt-4" data-testid="mfa-reauth-form">
      <p className={styles.totpNote}>{t(mode === "remove" ? "mfa.removeNote" : "mfa.reauthNote")}</p>
      {hasPassword && <Field className="mt-3" label={t("mfa.currentPassword")} type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} disabled={busy} maxLength={72} testId="mfa-reauth-password" />}
      <div className={styles.securityActions}><Button type="submit" busy={busy} disabled={busy || (hasPassword && !password)} data-testid="mfa-reauth-button">{t(hasPassword ? "mfa.continue" : "mfa.googleReauth")}</Button><Button variant="ghost" disabled={busy} onClick={reset}>{t("acc.photoCancel")}</Button></div>
    </form>}
    {challenge && <MfaChallenge challenge={challenge} onVerify={verifyReauth} onCancel={reset} />}
    {setup && <form onSubmit={enroll} className="mt-4" data-testid="mfa-enrollment-form">
      <p className={styles.totpNote}>{t("mfa.scanNote")}</p>
      {qr && <img src={qr} width={180} height={180} alt={t("mfa.qrAlt")} className={styles.mfaQr} />}
      <p className={styles.totpNote}>{t("mfa.manualKey")}</p><code className={styles.mfaKey}>{setup.secret.secretKey}</code>
      <Field className="mt-4" label={t("mfa.code")} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" maxLength={6} disabled={busy} testId="mfa-enroll-code" />
      <div className={styles.securityActions}><Button type="submit" busy={busy} disabled={busy || code.length !== 6}>{t("mfa.verify")}</Button><Button variant="ghost" disabled={busy} onClick={reset}>{t("acc.photoCancel")}</Button></div>
    </form>}
    {error && <p role="alert" className="mt-3 text-sm leading-5 text-ax-error" data-testid="mfa-error">{error}</p>}
    <p role="status" className={styles.flash}>{success}</p>
  </div>;
}
